import React, { useEffect, useState } from "react";
import styled from "styled-components";

function Questions(props) {
  const [messageIsHidden, setMessageIsHidden] = useState(props.isHidden);
  const [answers, setAnswers] = useState([]); // per-question: string | string[]
  const [isDiabled, setIsDiabled] = useState(props.isDiabled);
  const [countGrade, setCountGrade] = useState(0);

  // When the question set changes, initialize answers per question type
  useEffect(() => {
    const entries = Object.entries(props.questions || {});
    const initial = entries.map(([, q]) =>
      Array.isArray(q.answer) ? [] : null
    );
    setAnswers(initial);
    setCountGrade(0);
    props.buttonDisable(true);
  }, [props.questions]);

  // Keep local mirrors in sync
  useEffect(() => setMessageIsHidden(props.isHidden), [props.isHidden]);
  useEffect(() => setIsDiabled(props.isDiabled), [props.isDiabled]);

  // Recompute: submit enabled + grade
  useEffect(() => {
    const entries = Object.entries(props.questions || {});
    const total = entries.length;

    if (total === 0) {
      props.buttonDisable(true);
      setCountGrade(0);
      return;
    }

    // Is each question answered?
    const answeredCount = entries.reduce((acc, [, q], idx) => {
      const a = answers[idx];
      if (Array.isArray(q.answer)) {
        return acc + (Array.isArray(a) && a.length > 0 ? 1 : 0);
      }
      return acc + (a != null ? 1 : 0);
    }, 0);

    props.buttonDisable(answeredCount < total);

    // Grade: number correct
    const isEqualSet = (aArr, bArr) => {
      if (!Array.isArray(aArr) || !Array.isArray(bArr)) return false;
      if (aArr.length !== bArr.length) return false;
      const aSet = new Set(aArr);
      for (const v of bArr) if (!aSet.has(v)) return false;
      return true;
    };

    const correctCount = entries.reduce((acc, [, q], idx) => {
      const a = answers[idx];
      if (Array.isArray(q.answer)) {
        return acc + (isEqualSet(a, q.answer) ? 1 : 0);
      }
      return acc + (a === q.answer ? 1 : 0);
    }, 0);

    setCountGrade(correctCount);
  }, [answers, props.questions, props.buttonDisable]);

  // Convert countGrade => percentage for parent
  useEffect(() => {
    const total = Object.entries(props.questions || {}).length;
    if (total > 0) {
      props.grade((countGrade / total) * 100);
    }
  }, [countGrade, props.questions, props.grade]);

  // Handle selecting an option
  const handleChange = (e, qIndex, correctAnswer) => {
    const value = e.target.value; // option key like "o2"
    const multi = Array.isArray(correctAnswer);

    setAnswers(prev => {
      const next = [...prev];
      if (multi) {
        const current = Array.isArray(next[qIndex]) ? next[qIndex] : [];
        const exists = current.includes(value);
        next[qIndex] = exists
          ? current.filter(v => v !== value)
          : [...current, value];
      } else {
        next[qIndex] = value;
      }
      return next;
    });
  };

  // For multi-answer questions: count option-level correctness
  const perOptionScore = (q, selected) => {
    const optionKeys = Object.keys(q.options || {});
    const correctSet  = new Set(Array.isArray(q.answer) ? q.answer : []);
    const pickedSet   = new Set(Array.isArray(selected) ? selected : []);
    let correctCount = 0;

    for (const key of optionKeys) {
      const shouldPick = correctSet.has(key);
      const didPick    = pickedSet.has(key);
      if ((shouldPick && didPick) || (!shouldPick && !didPick)) {
        correctCount += 1; // this option is handled correctly
      }
    }
    return { correctCount, total: optionKeys.length };
  };

  const entries = Object.entries(props.questions || {});

  return (
    <Container>
      {entries.length ? (
        entries.map(([qid, q], idx) => {
          const isMulti = Array.isArray(q.answer);
          const inputType = isMulti ? "checkbox" : "radio";
          const selected = answers[idx];

          const isChecked = (optKey) =>
            isMulti
              ? Array.isArray(selected) && selected.includes(optKey)
              : selected === optKey;

          const isAnswered = isMulti
            ? Array.isArray(selected) && selected.length > 0
            : selected != null;

          const isCorrect = isMulti
            ? Array.isArray(selected) &&
              selected.length === q.answer.length &&
              q.answer.every(k => selected.includes(k))
            : selected === q.answer;

          return (
            <QuizContainer key={qid ?? idx}>
              <QuestionContainer>
                <QuestionNumber>{idx + 1})</QuestionNumber>
                <Question>{q.question}</Question>
              </QuestionContainer>

              <OptionContainer>
                {Object.entries(q.options).map(([optKey, label]) => (
                  <Option key={optKey}>
                    <RadioButtonContainer>
                      <input
                        type={inputType}
                        name={isMulti ? `options${idx}-${optKey}` : `options${idx}`}
                        value={optKey}
                        onChange={(e) => handleChange(e, idx, q.answer)}
                        checked={isChecked(optKey)}
                        disabled={isDiabled}
                      />
                    </RadioButtonContainer>
                    <div>{label}</div>
                  </Option>
                ))}
              </OptionContainer>

              <MessageContainer>
                <Message style={{ display: messageIsHidden ? "none" : "block" }}>
                    {//If the question has been answered or not, otherwise report as "Not Answered" 
                    isAnswered 
                      //Was the answer provided correct, if so identify the question as "Correct"
                    ? ( isCorrect 
                      ? ( <div style={{ color: "green" }}>Correct</div>) 
                      //If the question is an array and only partially correct, reports fraction of correctly selected 
                      : ( Array.isArray(q.answer) 
                          ? ( (() => {
                            const { correctCount, total } = perOptionScore(q, selected);
                            return ( <div style={{ color: "red" }}>Incorrect ({correctCount}/{total})</div>);
                          })()) 
                          //If only one possible option and was chosen incorrectly, acknowledge
                          : ( <div style={{ color: "red" }}>Incorrect</div> )
                        )) 
                    : (<div>Not Answered</div>)}
                </Message>
              </MessageContainer>
            </QuizContainer>
          );
        })
      ) : (
        <div />
      )}
    </Container>
  );
}

export default Questions;

/* --- styles (unchanged) --- */
const Container = styled.div``;
const Message = styled.div``;

const QuizContainer = styled.div``;
const QuestionContainer = styled.div`
  display: flex;
  margin-bottom: 10px;
`;
const QuestionNumber = styled.div`
  padding-right: 5px;
  font-weight: bold;
`;
const Question = styled.div`
  font-weight: bold;
`;

const OptionContainer = styled.div`
  padding-left: 30px;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
`;

const RadioButtonContainer = styled.div`
  padding-top: 4px;
  padding-right: 5px;
`;

const MessageContainer = styled.div`
  height: 30px;
`;